import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Grid,
  Snackbar,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  DialogContentText
} from '@mui/material';
import Alert from '@mui/material/Alert';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { registerUser, fetchUsers, updateUser, deleteUser } from '../services/api';

const UserManagement = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('admin'); // Changed to lowercase to match backend
  const [partnerName, setPartnerName] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  // New state for edit functionality
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editRole, setEditRole] = useState('');
  const [editPartnerName, setEditPartnerName] = useState('');

  // New state for delete functionality
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Fetch users when component mounts
  useEffect(() => {
    loadUsers();
  }, []);
  
  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const data = await fetchUsers();
      setUsers(data);
    } catch (error) {
      console.error('Failed to load users:', error);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoadingUsers(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Pass the new fields to the API call
      await registerUser(email, password, name, role, role !== 'admin' ? partnerName : null);
      setSuccess(true);
      setEmail('');
      setPassword('');
      setName('');
      setRole('admin');
      setPartnerName('');
      // Reload the user list after successful registration
      loadUsers();
    } catch (error) {
      setError(error.response.data.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // Function to handle opening the edit dialog
  const handleEditClick = (user) => {
    setEditUser(user);
    setEditName(user.display_name || '');
    setEditEmail(user.email || '');
    setEditRole(user.role || 'admin');
    setEditPartnerName(user.partner_name || '');
    setEditDialogOpen(true);
  };

  // Function to handle closing the edit dialog
  const handleEditClose = () => {
    setEditDialogOpen(false);
    setEditUser(null);
  };

  // Function to handle edit form submission
  const handleEditSubmit = async () => {
    if (!editUser) return;
    
    setEditLoading(true);
    try {
      const updatedData = {
        display_name: editName,
        email: editEmail,
        role: editRole,
        partner_name: editRole !== 'admin' ? editPartnerName : null
      };
      
      await updateUser(editUser.id, updatedData);
      setSuccess(true);
      handleEditClose();
      loadUsers(); // Reload the user list
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to update user');
    } finally {
      setEditLoading(false);
    }
  };

  // Function to handle opening the delete dialog
  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  // Function to handle closing the delete dialog
  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  // Function to handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    
    setDeleteLoading(true);
    try {
      await deleteUser(userToDelete.id);
      setSuccess(true);
      handleDeleteClose();
      loadUsers(); // Reload the user list
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to delete user');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Function to get color based on role
  const getRoleColor = (role) => {
    switch(role) {
      case 'admin':
        return 'primary';
      case 'referral_manager':
        return 'secondary';
      case 'affiliator':
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Create New User
      </Typography>
      
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} md={8}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
              <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Display Name"
                    variant="outlined"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    variant="outlined"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Password"
                    variant="outlined"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </Grid>
                
                {/* Role Selection Field */}
                <Grid item xs={12}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Role</InputLabel>
                    <Select
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                      label="Role"
                      required
                    >
                      <MenuItem value="admin">Admin</MenuItem>
                      <MenuItem value="referral_manager">Referral Manager</MenuItem>
                      <MenuItem value="affiliator">Affiliator</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                {/* Conditional Partner Name Field */}
                {role !== 'admin' && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Partner Name"
                      variant="outlined"
                      type="text"
                      value={partnerName}
                      onChange={(e) => setPartnerName(e.target.value)}
                      required
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <PersonAddIcon />}
                  >
                    {loading ? 'Registering...' : 'Register User'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Grid>
        </Grid>

        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError('')}
        >
          <Alert severity="error">{error}</Alert>
        </Snackbar>

        <Snackbar
          open={success}
          autoHideDuration={6000}
          onClose={() => setSuccess(false)}
        >
          <Alert severity="success">Operation completed successfully!</Alert>
        </Snackbar>
      </Paper>

      {/* User List Section */}
      <Typography variant="h5" sx={{ mb: 3, mt: 4 }}>
        Registered Users
      </Typography>
      
      <Paper elevation={2} sx={{ p: 3 }}>
        {loadingUsers ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : users.length > 0 ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Partner</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.display_name || '-'}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Chip 
                        label={user.role || 'user'} 
                        color={getRoleColor(user.role)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{user.partner_name || '-'}</TableCell>
                    <TableCell>{user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}</TableCell>
                    <TableCell>{user.last_login_at ? new Date(user.last_login_at).toLocaleDateString() : '-'}</TableCell>
                    <TableCell>
                      <IconButton 
                        size="small" 
                        color="primary" 
                        onClick={() => handleEditClick(user)}
                        title="Edit User"
                        sx={{ mr: 1 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        color="error" 
                        onClick={() => handleDeleteClick(user)}
                        title="Delete User"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body1" color="textSecondary">
              No users found. Register a new user to get started.
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Display Name"
                variant="outlined"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                variant="outlined"
                type="email"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                margin="normal"
                disabled // Email should not be editable as it's the primary identifier
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined" margin="normal">
                <InputLabel>Role</InputLabel>
                <Select
                  value={editRole}
                  onChange={(e) => setEditRole(e.target.value)}
                  label="Role"
                >
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="referral_manager">Referral Manager</MenuItem>
                  <MenuItem value="affiliator">Affiliator</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {editRole !== 'admin' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Partner Name"
                  variant="outlined"
                  value={editPartnerName}
                  onChange={(e) => setEditPartnerName(e.target.value)}
                  margin="normal"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleEditSubmit} 
            color="primary" 
            variant="contained"
            disabled={editLoading}
            startIcon={editLoading ? <CircularProgress size={20} /> : null}
          >
            {editLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteClose}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete User
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete the user <strong>{userToDelete?.display_name || userToDelete?.email}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={20} /> : null}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;